import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    useTheme,
    Stack,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import Button from '../../components/button';
import Dropdown from '../../components/dropdown';
import InputField from '../../components/formItem/inputField';
import BoxData from '../../components/boxData';
import { SortableHeaderCell, HeaderCell } from '../../components/table/headerCell';
import { getTableStyles } from '../../components/table/styles';
import { Search, Prev, Next } from '../../assets/images';
import { ORGANIZATION_TYPES, COUNTRY_OPTIONS, getRegionOptions, API_PAGE_SIZE } from '../../constants';
import { formatDateTimeDisplay, createSortConfig, handleSort, formatTime, toDisplayString } from '../../utils/base';
import { organizationAPI } from '../../actions/imsServer';

const OrganizationList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searching, setSearching] = useState(false); // 검색 중 상태 (테이블은 유지)
    const [error, setError] = useState(null);
    const [filterValue, setFilterValue] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const ITEMS_PER_PAGE = API_PAGE_SIZE;

    // 검색 필드 상태
    const [searchFields, setSearchFields] = useState({
        organizationName: '',
        country: '',
        region: '',
        organizationType: ''
    });

    // 정렬 상태 (초기값: exp 내림차순 - exp가 높을수록 랭킹이 높음)
    const [sortConfig, setSortConfig] = useState(createSortConfig('exp', 'desc'));

    // API에서 기관 목록 가져오기
    const fetchOrganizations = async (searchParams = {}, showLoading = true, page = 1, currentSortConfig = sortConfig) => {
        try {
            if (showLoading) {
                setLoading(true);
            }
            setError(null);

            // 각 API를 개별적으로 호출하여 하나가 실패해도 다른 것들은 처리되도록 함
            let organizationsData = [];

            try {
                // 검색 파라미터 준비
                const offset = (page - 1) * ITEMS_PER_PAGE;
                const apiParams = {
                    state: 0,
                    expand: 'memberships,didims,playInfo',
                    offset: offset,
                    limit: ITEMS_PER_PAGE,
                    ...searchParams
                };

                // 서버사이드 정렬 파라미터 추가
                // 초기화면은 경험치(exp) 기준으로 내림차순 정렬
                if (currentSortConfig.key) {
                    apiParams.sort = currentSortConfig.key;
                    apiParams.order = currentSortConfig.direction === 'asc' ? 'asc' : 'desc';
                } else {
                    // 정렬이 설정되지 않은 경우 기본값: exp 내림차순
                    apiParams.sort = 'exp';
                    apiParams.order = 'desc';
                }

                // 검색 파라미터 포함하여 API 호출
                const organizationsResponse = await organizationAPI.getOrganizations(apiParams);
                organizationsData = organizationsResponse?.data?.organizations || organizationsResponse?.organizations || [];
                const total = organizationsResponse?.data?.total || organizationsResponse?.total || 0;

                setTotalCount(total);
            } catch (err) {
                console.error('Failed to fetch organizations:', err);
                setError(t('failed_to_load_organization_list'));
                setOrganizations([]);
                setTotalCount(0);
            }

            // exp로 정렬된 데이터를 기반으로 랭킹 계산
            // 서버에서 exp로 정렬된 데이터를 받았으므로, offset을 고려하여 랭킹 계산

            // 기관별 사용자 수, 디딤 ID, 금일총사용 계산
            // expand로 받아온 memberships와 didims 데이터를 우선 사용
            let organizationsWithData = organizationsData.map(org => {
                // expand로 받아온 memberships 사용 (배열인지 확인 후 처리)
                // expand로 받아온 didims 사용 (배열인지 확인 후 처리)
                const orgDidims = Array.isArray(org.didims) ? org.didims : [];
                const didimIds = orgDidims.length > 0
                    ? orgDidims.map(didim => {
                        // didim이 객체면 id를, 아니면 그대로 사용
                        return typeof didim === 'object' && didim !== null ? (didim.id || didim) : didim;
                    }).join(',')
                    : '0';

                // playInfo.todayPlayTime에서 금일 사용 시간 가져오기 (분 단위)
                const dailyUsageMin = org.playInfo?.todayPlayTime ? Math.round(org.playInfo.todayPlayTime) : 0;

                return {
                    ...org,
                    didimId: didimIds,
                    dailyUsage: dailyUsageMin
                };
            });

            // 랭킹은 항상 exp 기준으로 계산되어야 함
            // 다른 컬럼을 정렬해도 랭킹은 exp 기준으로 유지되어야 하므로,
            // exp 기준으로 정렬된 전체 데이터를 별도로 가져와서 랭킹을 계산
            // 스펙 아웃 - 주석 처리
            // let expRankingMap = new Map();
            // try {
            //     // exp 기준으로 정렬된 전체 데이터 가져오기 (랭킹 계산용)
            //     const expRankingParams = {
            //         state: 0,
            //         sort: 'exp',
            //         order: 'desc',
            //         limit: 10000 // 충분히 큰 값으로 전체 데이터 가져오기
            //     };
            //     const expRankingResponse = await organizationAPI.getOrganizations(expRankingParams);
            //     const expRankingData = expRankingResponse?.data?.organizations || expRankingResponse?.organizations || [];
            //     
            //     // exp 기준으로 정렬된 데이터를 기반으로 랭킹 계산
            //     expRankingData.forEach((org, idx) => {
            //         expRankingMap.set(org.id, idx + 1);
            //     });
            // } catch (err) {
            //     console.error('Failed to fetch exp ranking data:', err);
            // }

            // 랭킹 정보를 기관 데이터에 추가
            // 스펙 아웃 - 주석 처리
            // const organizationsWithRanking = organizationsWithData.map((org) => {
            //     // exp 기준 랭킹 맵에서 랭킹 가져오기
            //     const ranking = expRankingMap.get(org.id) || 0;

            //     return {
            //         ...org,
            //         ranking: ranking
            //     };
            // });

            // setOrganizations(organizationsWithRanking);
            setOrganizations(organizationsWithData);
        } catch (err) {
            console.error('Failed to fetch organizations:', err);
            setError(t('failed_to_load_organization_list'));
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    };

    // 검색 파라미터 생성 함수
    const getSearchParams = useCallback(() => {
        let searchParams = {};

        if (filterValue === 'search_by_org_name') {
            if (searchFields.organizationName.trim().length > 0) {
                searchParams.keyword = searchFields.organizationName.trim();
            }
        } else if (filterValue === 'search_by_country_region') {
            // 국가만 선택한 경우 해당 국가 전체 검색
            if (searchFields.country) {
                searchParams.country = searchFields.country;
            }
            // 지역이 선택된 경우에만 region 파라미터 추가
            if (searchFields.region) {
                searchParams.region = searchFields.region;
            }
        } else if (filterValue === 'search_by_org_type') {
            if (searchFields.organizationType !== '') {
                searchParams.type = parseInt(searchFields.organizationType);
            }
        }

        return searchParams;
    }, [filterValue, searchFields]);

    // 페이지 변경 핸들러
    const handlePageChange = useCallback((newPage) => {
        if (newPage < 1) return;
        const maxPage = Math.ceil(totalCount / ITEMS_PER_PAGE);
        if (newPage > maxPage && maxPage > 0) return;

        setCurrentPage(newPage);
        const searchParams = getSearchParams();
        fetchOrganizations(searchParams, true, newPage, sortConfig);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchOrganizations는 의존성에서 제외
    }, [totalCount, getSearchParams, sortConfig]);

    // 이전 페이지
    const handlePrevPage = useCallback(() => {
        if (currentPage > 1) {
            handlePageChange(currentPage - 1);
        }
    }, [currentPage, handlePageChange]);

    // 다음 페이지
    const handleNextPage = useCallback(() => {
        const maxPage = Math.ceil(totalCount / ITEMS_PER_PAGE);
        if (currentPage < maxPage) {
            handlePageChange(currentPage + 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchOrganizations는 의존성에서 제외
    }, [currentPage, totalCount, handlePageChange]);

    // 컴포넌트 마운트 시 데이터 로드 (fetchOrganizations는 매 렌더 새 참조라 의존성에서 제외)
    useEffect(() => {
        setOrganizations([]);
        setCurrentPage(1);
        setTotalCount(0);
        fetchOrganizations({}, true, 1, sortConfig);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- 마운트 시 1회만 실행
    }, []);

    // 검색 실행 함수 (useCallback으로 메모이제이션하여 불필요한 리렌더링 방지)
    const handleSearch = useCallback(async () => {
        try {
            setSearching(true); // 검색 중 상태만 설정 (테이블은 유지)
            setError(null);

            // 검색 파라미터 준비
            let searchParams = {};

            if (filterValue === 'search_by_org_name') {
                // 기관명 검색 시 keyword 파라미터 사용
                if (searchFields.organizationName.trim().length > 0) {
                    searchParams.keyword = searchFields.organizationName.trim();
                }
            } else if (filterValue === 'search_by_country_region') {
                // 국가만 선택한 경우 해당 국가 전체 검색
                if (searchFields.country) {
                    searchParams.country = searchFields.country;
                }
                // 지역이 선택된 경우에만 region 파라미터 추가
                if (searchFields.region) {
                    searchParams.region = searchFields.region;
                }
            } else if (filterValue === 'search_by_org_type') {
                // 기관유형 필터는 type 파라미터 사용
                if (searchFields.organizationType !== '') {
                    searchParams.type = parseInt(searchFields.organizationType);
                }
            }

            // 데이터 초기화 후 검색
            setOrganizations([]);
            setCurrentPage(1);
            setTotalCount(0);
            await fetchOrganizations(searchParams, false, 1, sortConfig);

        } catch (err) {
            console.error('Failed to search organizations:', err);
            setError(t('an_error_occurred_while_searching_organizations'));
        } finally {
            setSearching(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchOrganizations는 의존성에서 제외
    }, [filterValue, searchFields.organizationName, searchFields.country, searchFields.region, searchFields.organizationType, sortConfig]);

    const handleEditOrganization = (e, organization) => {
        // 이벤트 전파 중지 (row 클릭 이벤트와 충돌 방지)
        e.stopPropagation();
        // 기관 수정 페이지로 이동 (데이터 전달)
        navigate('/organizations/edit', {
            state: {
                organization: organization
            }
        });
    };

    const handleViewDetail = (organization) => {
        // 기관 상세 페이지로 이동
        navigate(`/organizations/detail`, {
            state: {
                organization: organization
            }
        });
    };

    const handleAddOrganization = () => {
        navigate('/organizations/edit');
    };

    // 정렬 함수
    const onSort = (key) => {
        const newSortConfig = handleSort(sortConfig, key);
        setSortConfig(newSortConfig);
        // 정렬 시 첫 페이지로 이동하고 데이터 다시 로드
        setCurrentPage(1);
        const searchParams = getSearchParams();
        fetchOrganizations(searchParams, true, 1, newSortConfig);
    };

    // 검색 필터 변경 시 검색 필드만 리셋 (API 호출하지 않음)
    useEffect(() => {
        // 검색 필드 리셋
        setSearchFields({
            organizationName: '',
            country: '',
            region: '',
            organizationType: ''
        });

        // "전체"로 변경했을 때만 전체 데이터 불러오기
        if (filterValue === 'all') {
            setOrganizations([]);
            setCurrentPage(1);
            setTotalCount(0);
            fetchOrganizations({}, true, 1, sortConfig);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- filterValue/sortConfig 변경 시에만 재실행
    }, [filterValue, sortConfig]);

    // 검색 필드 변경 시 자동 필터링 (기관명 검색 제외 - 엔터키 또는 돋보기 클릭 시에만 검색)
    useEffect(() => {
        // 기관명 검색은 자동 검색하지 않음 (엔터키 또는 돋보기 클릭 시에만 검색)
        // 국가/지역별 검색: 국가만 선택해도 검색 실행 (해당 국가 전체)
        if (filterValue === 'search_by_country_region' && searchFields.country) {
            handleSearch();
        } else if (filterValue === 'search_by_org_type' && searchFields.organizationType !== '') {
            handleSearch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- handleSearch는 의존성에서 제외(반복 호출 방지)
    }, [searchFields.country, searchFields.region, searchFields.organizationType, filterValue]);

    // 검색 필드 렌더링 함수 (useMemo로 메모이제이션하여 포커스 유지)
    const renderSearchFields = useMemo(() => {
        switch (filterValue) {
            case 'search_by_org_name':
                return (
                    <Box sx={{
                        display: 'flex', alignItems: 'center', alignSelf: 'stretch', gap: 2, flex: 0.5
                    }}>
                        <InputField
                            key="organization-name-search"
                            type="text"
                            placeholder={t('please_enter_organization_name')}
                            value={searchFields.organizationName}
                            onChange={(e) => setSearchFields(prev => ({ ...prev, organizationName: e.target.value }))}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSearch();
                                }
                            }}
                            onBlur={() => {
                                // 포커스 아웃 시 검색 실행
                                handleSearch();
                            }}
                            showClearIcon={true}
                            onClear={() => setSearchFields(prev => ({ ...prev, organizationName: '' }))}
                        />
                    </Box>
                );

            case 'search_by_country_region':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                        <Dropdown
                            options={COUNTRY_OPTIONS}
                            value={searchFields.country}
                            onChange={(option) => {
                                setSearchFields(prev => ({ ...prev, country: option.value }));
                            }}
                            placeholder={t('select_country')}
                        />
                        <Dropdown
                            options={getRegionOptions(searchFields.country)}
                            value={searchFields.region}
                            onChange={(option) => {
                                setSearchFields(prev => ({ ...prev, region: option.value }));
                            }}
                            placeholder={t('select_region')}
                        />
                    </Box>
                );

            case 'search_by_org_type':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                        <Dropdown
                            options={ORGANIZATION_TYPES}
                            value={searchFields.organizationType}
                            onChange={(option) => {
                                setSearchFields(prev => ({ ...prev, organizationType: option.value }));
                            }}
                            placeholder={t('please_select')}
                        />
                    </Box>
                );

            default:
                return null;
        }
    }, [filterValue, searchFields.organizationName, searchFields.country, searchFields.region, searchFields.organizationType, handleSearch, t]);

    return (
        <ContentArea>
            {/* 로딩 상태 */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <Typography>{t('loading_organization_list')}</Typography>
                </Box>
            )}

            {/* 에러 상태 */}
            {error && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <Typography color="error">{error}</Typography>
                </Box>
            )}

            {/* 검색 영역 */}
            {!loading && (
                <Box sx={{
                    display: 'flex',
                    width: '100%',
                    px: 2.5,
                    py: 2.5,
                    mb: 3
                }}>
                    {/* 검색 타입 선택 */}
                    <Box sx={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        mb: 2,
                        flex: 1,
                        alignSelf: 'stretch'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                            <Dropdown
                                options={[
                                    { value: 'all', label: 'all' },
                                    { value: 'search_by_org_name', label: 'search_by_org_name' },
                                    { value: 'search_by_country_region', label: 'search_by_country_region' },
                                    { value: 'search_by_org_type', label: 'search_by_organization_type' },
                                ]}
                                value={filterValue}
                                onChange={(option) => setFilterValue(option.value)}
                                placeholder={t('select_search_type')}
                            />

                            {/* 검색 필드 */}
                            {renderSearchFields}

                            <IconButton
                                onClick={handleSearch}
                                sx={{
                                    display: filterValue === 'search_by_org_name' ? 'block' : 'none',
                                    width: '24px',
                                    height: '24px',
                                    p: 0,
                                    color: theme.palette.figma.iconWhite,
                                    '&:hover': {
                                        backgroundColor: theme.palette.figma.white[20],
                                    }
                                }}
                            >
                                <Search />
                            </IconButton>
                        </Box>

                        <Stack direction="row" spacing={1} alignItems="center">
                            {/* 2순위 */}
                            {/* <SettingsIcon sx={{ color: theme.palette.figma.white[60] }} />
                        <Typography variant="body2" sx={{ color: theme.palette.figma.white[60] }}>
                            기관유형, 국가등 설정
                        </Typography> */}
                        </Stack>
                    </Box>
                </Box>
            )}

            {/* 테이블 */}
            {!loading && (
                <Box
                    sx={{
                        px: 2.5,
                        mb: 3,
                        opacity: searching ? 0.7 : 1,
                        transition: 'opacity 0.2s ease-in-out',
                    }}
                >
                    <TableContainer component={Paper}>
                        <Table aria-label="organization table">
                            <TableHead>
                                <TableRow>
                                    {/* 스펙 아웃 - 주석 처리 */}
                                    {/* <SortableHeaderCell
                                        columnKey="exp"
                                        label="랭킹"
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    /> */}
                                    <SortableHeaderCell
                                        columnKey="name"
                                        label={t('organization_name')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <SortableHeaderCell
                                        columnKey="country"
                                        label={t('country')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <SortableHeaderCell
                                        columnKey="region"
                                        label={t('region')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <SortableHeaderCell
                                        columnKey="type"
                                        label={t('organization_type')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <HeaderCell label={t('division_1')} align="center" />
                                    <HeaderCell label={t('division_2')} align="center" />
                                    <SortableHeaderCell
                                        columnKey="membershipCount"
                                        label={t('total_members')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <HeaderCell label={t('didim_id')} align="center" />
                                    <SortableHeaderCell
                                        columnKey="point"
                                        label={t('available_points')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <SortableHeaderCell
                                        columnKey="exp"
                                        label={t('exp')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <HeaderCell label={t('today_total_usage')} align="center" />
                                    <SortableHeaderCell
                                        columnKey="id"
                                        label={t('organization_id')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <SortableHeaderCell
                                        columnKey="createDate"
                                        label={t('organization_created_date')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <HeaderCell label={t('edit')} align="center" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.isArray(organizations) && organizations.length > 0 ? organizations.map((org) => (
                                    <TableRow
                                        key={org.id}
                                        onClick={() => handleViewDetail(org)}
                                        sx={{
                                            cursor: 'pointer',
                                            ...getTableStyles(theme).tableRowStyle
                                        }}
                                    >
                                        {/* 스펙 아웃 - 주석 처리 */}
                                        {/* <TableCell sx={{ textAlign: 'right' }}>{org.ranking}</TableCell> */}
                                        <TableCell sx={{}}>{org.name}</TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            {org.country === 'ko' ? t('south_korea') : org.country}
                                        </TableCell>
                                        <TableCell>
                                            {(() => { const r = getRegionOptions(org.country).find(reg => reg.value === org.region); return r ? toDisplayString(t(r.label), r.label) : org.region; })()}
                                        </TableCell>
                                        <TableCell>
                                            <BoxData
                                                data={(() => { const typ = ORGANIZATION_TYPES.find(typeItem => typeItem.value === org.type); return typ ? toDisplayString(t(typ.label), typ.label) : org.type; })()}
                                                variant="default"
                                            />
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            {org.divisionSections ? Object.keys(org.divisionSections).length : 0}
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            {org.divisionSections ? Object.values(org.divisionSections).map(sections => sections.length).join(',') : ''}
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'right' }}>{org.memberCount?.toLocaleString() || 0}</TableCell>
                                        <TableCell sx={{ textAlign: 'right' }}>{org.didimId || '0'}</TableCell>
                                        <TableCell sx={{ textAlign: 'right' }}>{org.point?.toLocaleString() || 0}</TableCell>
                                        <TableCell sx={{ textAlign: 'right' }}>{org.exp?.toLocaleString() || 0}</TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>{formatTime(org.dailyUsage, t) || ''}</TableCell>
                                        <TableCell sx={{ textAlign: 'right' }}>{org.id}</TableCell>
                                        <TableCell>{formatDateTimeDisplay(org.createDate, 'date')}</TableCell>
                                        <TableCell>
                                            <Button
                                                variant="normal"
                                                size="small"
                                                onClick={(e) => handleEditOrganization(e, org)}
                                            >
                                                {t('edit')}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={14} sx={{ textAlign: 'center', py: 4 }}>
                                            {loading ? t('loading_data') : t('no_organization_data')}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* 페이징 컨트롤 */}
                    {totalCount > 0 && (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '20px 0',
                        }}>
                            <Box
                                onClick={handlePrevPage}
                                sx={{
                                    width: '20px',
                                    height: '20px',
                                    cursor: currentPage > 1 ? 'pointer' : 'default',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: currentPage > 1 ? 1 : 0.3,
                                }}
                            >
                                <Prev color={theme.palette.figma.white[80]} size={20} />
                            </Box>
                            <Typography sx={{
                                fontSize: '16px',
                                fontWeight: 500,
                                color: theme.palette.figma.white[80],
                                letterSpacing: '-0.48px',
                                whiteSpace: 'nowrap'
                            }}>
                                {currentPage} / {Math.ceil(totalCount / ITEMS_PER_PAGE)}
                            </Typography>
                            <Box
                                onClick={handleNextPage}
                                sx={{
                                    width: '20px',
                                    height: '20px',
                                    cursor: currentPage < Math.ceil(totalCount / ITEMS_PER_PAGE) ? 'pointer' : 'default',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: currentPage < Math.ceil(totalCount / ITEMS_PER_PAGE) ? 1 : 0.3,
                                }}
                            >
                                <Next color={theme.palette.figma.white[80]} size={20} />
                            </Box>
                        </Box>
                    )}
                </Box>
            )}

            {/* 하단 기관추가 버튼 */}
            {!loading && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2.5, pb: 3, alignSelf: 'stretch' }}>
                    <Button
                        onClick={handleAddOrganization}
                    >
                        {t('add_organization')}
                    </Button>
                </Box>
            )}
        </ContentArea>
    );
};

export default OrganizationList;
